import { IKeyword, IVisibleKeyword, keywordAffiliation } from 'types';
import { cdmApi } from '.';

const PREFIX = '/keywords';
/* eslint-disable  @typescript-eslint/no-non-null-assertion */

const keywordsApi = cdmApi.injectEndpoints({
    endpoints: (builder) => ({
        getClientKeywords: builder.query<{ code: number; keywords: IKeyword[] }, void>({
            query: (params) => ({ url: `${PREFIX}?isTyped=false`, method: 'get', params }),
        }),

        getClientKeywordsByType: builder.query<{ code: number; keywords: IKeyword[] }, { type: keywordAffiliation }>({
            query: (params) => ({ url: `${PREFIX}/`, method: 'get', params }),
        }),
        exportClientKeywords: builder.query<any, { types: keywordAffiliation[]; isTemplate?: boolean }>({
            query: (params) => ({
                url: `${PREFIX}/excel/export`,
                method: 'get',
                params,
                responseType: 'arraybuffer',
            }),
        }),
        updateClientKeywords: builder.mutation<
            { code: number; updated: IKeyword },
            { keyword: IKeyword; type: keywordAffiliation }
        >({
            query: (data) => ({ url: `${PREFIX}/update`, method: 'put', data: data.keyword }),
            async onQueryStarted({ keyword, type }, { dispatch, queryFulfilled }) {
                const { data: updatedKeywords } = await queryFulfilled;
                const patchResult = dispatch(
                    keywordsApi.util.updateQueryData('getClientKeywordsByType', { type }, (draft) => {
                        const draftRef = draft;
                        const keywordToDelete = keyword.id;
                        const newKeywords = draftRef.keywords.filter((k) => k.id !== keywordToDelete);
                        if (updatedKeywords.updated[`for_${type}`]) newKeywords.push(updatedKeywords.updated);
                        draftRef.keywords = newKeywords;
                    }),
                );

                const secondType = type === 'search' ? 'banner' : 'search';
                const patchSecondResult = dispatch(
                    keywordsApi.util.updateQueryData('getClientKeywordsByType', { type: secondType }, (draft) => {
                        const draftRef = draft;
                        const keywordToDelete = keyword.id;
                        const newKeywords = draftRef.keywords.filter((k) => k.id !== keywordToDelete);
                        if (updatedKeywords.updated[`for_${secondType}`]) newKeywords.push(updatedKeywords.updated);
                        draftRef.keywords = newKeywords;
                    }),
                );

                queryFulfilled.catch(() => {
                    patchResult.undo();
                    patchSecondResult.undo();
                });
            },
        }),
        addClientKeyword: builder.mutation<
            { code: number; message: string; keyword: IKeyword },
            { keyword: IVisibleKeyword; type: keywordAffiliation }
        >({
            query: (data) => ({ url: `${PREFIX}/add`, method: 'POST', data: data.keyword }),
            async onQueryStarted({ type }, { dispatch, queryFulfilled }) {
                const {
                    data: { keyword: newKeyword },
                } = await queryFulfilled;
                const patchResult = dispatch(
                    keywordsApi.util.updateQueryData('getClientKeywordsByType', { type }, (draft) => {
                        const draftRef = draft;
                        draftRef.keywords.push(newKeyword);
                    }),
                );

                const secondType = type === 'search' ? 'banner' : 'search';
                const patchSecondResult =
                    newKeyword[`for_${secondType}`] &&
                    dispatch(
                        keywordsApi.util.updateQueryData('getClientKeywordsByType', { type: secondType }, (draft) => {
                            const draftRef = draft;
                            draftRef.keywords = draft.keywords.filter((k) => k.keyword_id !== newKeyword.keyword_id);
                            draftRef.keywords.push(newKeyword);
                        }),
                    );

                queryFulfilled.catch(() => {
                    patchResult.undo();
                    if (patchSecondResult) patchSecondResult.undo();
                });
            },
        }),

        deleteClientKeywords: builder.mutation<
            { code: number; nonExisitingIds?: number[] },
            { keywords: { id: number; type: 'search' | 'banner' }[] }
        >({
            query: (data) => ({ url: `${PREFIX}/delete`, method: 'DELETE', data: data.keywords }),
            onQueryStarted({ keywords }, { dispatch, queryFulfilled }) {
                const searchKeywords = keywords.filter((k) => k.type === 'search').map((k) => k.id);
                const bannerKeywords = keywords.filter((k) => k.type === 'banner').map((k) => k.id);

                const patchResult = dispatch(
                    keywordsApi.util.updateQueryData('getClientKeywordsByType', { type: 'search' }, (draft) => {
                        const draftRef = draft;
                        draftRef.keywords = draftRef.keywords
                            .filter((k) => !searchKeywords.includes(k.keyword_id))
                            .map((k) => {
                                const keywordRef = k;
                                if (bannerKeywords.includes(keywordRef.keyword_id)) {
                                    keywordRef.for_banner = false;
                                }
                                return keywordRef;
                            });
                    }),
                );

                const patchResultSecond = dispatch(
                    keywordsApi.util.updateQueryData('getClientKeywordsByType', { type: 'banner' }, (draft) => {
                        const draftRef = draft;
                        draftRef.keywords = draftRef.keywords
                            .filter((k) => !bannerKeywords.includes(k.keyword_id))
                            .map((k) => {
                                const keywordRef = k;
                                if (searchKeywords.includes(keywordRef.keyword_id)) {
                                    keywordRef.for_search = false;
                                }
                                return keywordRef;
                            });
                    }),
                );

                queryFulfilled.catch(() => {
                    if (patchResult) patchResult.undo();
                    if (patchResultSecond) patchResultSecond.undo();
                });
            },
        }),

        uploadClientKeywords: builder.mutation<
            { code: number; created: IKeyword[]; updated: IKeyword[]; notAccepted: IKeyword[] },
            FormData
        >({
            query: (data) => ({ url: `${PREFIX}/excel/upload`, method: 'POST', data }),
            async onQueryStarted(data, { dispatch, queryFulfilled }) {
                const types: string[] = JSON.parse(data.get('keywordTypes') as string);
                await queryFulfilled;
                const refetchType1 = dispatch(
                    keywordsApi.endpoints.getClientKeywordsByType.initiate(
                        { type: types[0] as keywordAffiliation },
                        { forceRefetch: true },
                    ),
                );
                const secondType = types[0] === 'search' ? 'banner' : 'search';
                const refetchType2 = dispatch(
                    keywordsApi.endpoints.getClientKeywordsByType.initiate(
                        { type: secondType as keywordAffiliation },
                        { forceRefetch: true },
                    ),
                );

                refetchType1.catch(() => console.log('error refetching'));
                refetchType2.catch(() => console.log('error refetching second type'));
            },
        }),
        exportNonAcceptedKeywords: builder.mutation<any, IKeyword[]>({
            query: (data) => ({
                url: `${PREFIX}/excel/error`,
                method: 'POST',
                data,
                responseType: 'arraybuffer',
            }),
        }),
    }),
    overrideExisting: false,
});

export const {
    useGetClientKeywordsQuery,
    useGetClientKeywordsByTypeQuery,
    useLazyExportClientKeywordsQuery,
    useUpdateClientKeywordsMutation,
    useDeleteClientKeywordsMutation,
    useAddClientKeywordMutation,
    useUploadClientKeywordsMutation,
    useExportNonAcceptedKeywordsMutation,
} = keywordsApi;
